<script setup lang="ts">
import { computed } from 'vue';

import type { BaseHeaderImage, BaseHeaderTag } from './baseHeader.types';

const props = withDefaults(
  defineProps<{
    /** HTML from CKEditor; use {{img1}} or {{image1}}, matching the objects order. */
    content: string;
    objects?: BaseHeaderImage[];
    contentClass?: string;
    unwrapOuterParagraph?: boolean;
    as?: BaseHeaderTag;
  }>(),
  {
    objects: () => [],
    contentClass: '',
    unwrapOuterParagraph: true,
    as: 'div',
  },
);

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function unwrapSingleParagraph(html: string): string {
  const trimmed = html.trim();
  const match = trimmed.match(/^<p(\s[^>]*)?>([\s\S]*)<\/p>$/i);
  return match ? match[2].trim() : html;
}

function buildChip(obj: BaseHeaderImage | Record<string, unknown> | undefined): string {
  const o = (obj && typeof obj === 'object' ? obj : {}) as Record<string, unknown>;
  const rawImg = o.img ?? o.image;
  const rawBg = o.bg ?? o.bgColor;
  const src = typeof rawImg === 'string' ? rawImg.trim() : '';
  if (!src) return '';
  const alt = escapeHtmlAttr(typeof o.alt === 'string' ? o.alt.trim() : '');
  const bg = typeof rawBg === 'string' ? rawBg.trim() : '';
  const chipClass = 'base-header__chip inline-flex w-[70px] shrink-0 items-center justify-center overflow-hidden rounded-full align-middle sm:w-[80px] lg:w-[70px]';
  const imgClass = 'base-header__chip-img min-h-0 min-w-0 object-cover align-middle';
  const bgAttr = bg !== '' ? ` style="background-color:${escapeHtmlAttr(bg)}"` : '';
  return `<span class="${chipClass}"${bgAttr}><img class="${imgClass}" src="${escapeHtmlAttr(src)}" alt="${alt}" loading="lazy" decoding="async" /></span>`;
}

const renderedHtml = computed(() => {
  let html = props.content ?? '';
  if (props.unwrapOuterParagraph) {
    html = unwrapSingleParagraph(html);
  }
  const list = props.objects ?? [];
  for (let i = 0; i < list.length; i += 1) {
    const n = i + 1;
    const chip = buildChip(list[i]);
    const tokenImg = `{{img${n}}}`;
    const tokenImage = `{{image${n}}}`;
    if (html.includes(tokenImg)) {
      html = html.split(tokenImg).join(chip);
    } else if (html.includes(tokenImage)) {
      html = html.split(tokenImage).join(chip);
    }
  }
  return html
    .replace(/\{\{img\d+\}\}/g, '')
    .replace(/\{\{image\d+\}\}/g, '');
});
</script>

<template>
  <component :is="as">
    <span
      class="base-header leading-[inherit] [&_.base-header__chip-img]:[vertical-align:middle] [&_.base-header__chip]:box-border"
      :class="contentClass"
      v-html="renderedHtml"
    />
  </component>
</template>
